import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { List as ImmutableList } from 'immutable';
import { IActionRequestStatusImt } from 'common/state/interfaces';
import { IFilterSettings } from 'common/interfaces/filter';
import { IBodyCell, IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IResourceListItem, IResourceListItemImt } from 'modules/booking/interfaces';
import { IPageMetaImt } from 'common/interfaces/pagination';
import { ActionsMenu, ChangeStatusModal, Table } from 'common/components';
import { TableOrderByParams } from 'common/constants/table';
import { HEADER_ACTIONS_OPTION } from 'constants/table.constants';
import SmallScreenCell from './SmallScreenCell/SmallScreenCell';
import tableHeaders from 'common/messages/tableHeaders';
import commonMessages from 'common/messages/messages';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import ActionItemEdit from 'components/ActionItems/ActionItemEdit';
import ActionItemChangeStatus from 'components/ActionItems/ActionItemChangeStatus';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';

interface IResourcesTable {
  tableParams: ITableParams;
  tableFilterSettings: IFilterSettings[];
  items: ImmutableList<IResourceListItemImt>;
  meta: IPageMetaImt;
  isLoading: boolean;
  onChangeParams: (tableParams: ITableParams) => void;
  changeStatusResourceAction: IActionRequestStatusImt;
  changeResourceStatus: (resourceId: string, isActive: boolean) => void;
  resetResourceChangeStatus: () => void;
}

const ResourcesTable: FC<IResourcesTable> = ({
  isLoading,
  items,
  meta,
  onChangeParams,
  tableParams,
  tableFilterSettings,
  changeResourceStatus,
  changeStatusResourceAction,
  resetResourceChangeStatus,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const isStatusChangeLoading = changeStatusResourceAction.get('isLoading');

  const [selectedRow, setSelectedRow] = useState<IResourceListItem | null>(null);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState<boolean>(false);

  const {
    BOOKING_RESOURCES_CREATE,
    BOOKING_RESOURCES_EDIT,
    BOOKING_RESOURCES_CHANGE_STATUS,
  } = usePermissionContext();

  const headerOptions: IHeadCell[] = [
    {
      id: TableOrderByParams.NAME,
      label: intl.formatMessage(tableHeaders.name),
      sort: true,
      padding: 'none',
    },
    {
      id: TableOrderByParams.EMPLOYEE,
      label: intl.formatMessage(tableHeaders.employee),
      sort: true,
    },
    { id: TableOrderByParams.CLUB, label: intl.formatMessage(tableHeaders.clubs), sort: true },
    {
      id: TableOrderByParams.ACTIVE,
      label: intl.formatMessage(tableHeaders.status),
      sort: true,
    },
  ];

  if (BOOKING_RESOURCES_EDIT || BOOKING_RESOURCES_CHANGE_STATUS) {
    headerOptions.push(HEADER_ACTIONS_OPTION);
  }

  const handleEditClick = useCallback(
    (resourceId: string) => {
      navigate(`/booking/resources/${resourceId}/edit`);
    },
    [navigate],
  );

  const handleChangeStatusDialogShow = (resource: IResourceListItem) => {
    setSelectedRow(resource);
    setShowChangeStatusDialog(true);
  };

  const handleChangeStatusResource = () => {
    if (selectedRow) {
      changeResourceStatus(selectedRow.id, !selectedRow.active);
    }
  };

  const handleChangeStatusDialogClose = () => {
    setShowChangeStatusDialog(false);
  };

  useEffect(() => {
    if (changeStatusResourceAction.getIn(['result', 'success'])) {
      resetResourceChangeStatus();
      setShowChangeStatusDialog(false);
    }
  }, [changeStatusResourceAction, resetResourceChangeStatus]);

  const tableRows: ITableRow[] = items
    .map(
      (resource: IResourceListItemImt): ITableRow => {
        const employee = resource.get('employee');
        let btnEdit: JSX.Element | null = null;
        let btnChangeStatus: JSX.Element | null = null;

        const bodyCells: IBodyCell[] = [
          {
            label: resource.get('label'),
            className: 'body-cell',
            variant: 'subtitle2',
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: employee ? <EmployeeLinkWithAvatar employee={employee.toJS()} /> : null,
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: (
              <>
                {resource.get('clubs').map(club => (
                  <Typography key={club}>{club}</Typography>
                ))}
              </>
            ),
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: (
              <Typography
                variant="body1"
                color={resource.get('active') ? 'primary' : 'textSecondary'}
              >
                {resource.get('active')
                  ? intl.formatMessage(commonMessages.activeOption)
                  : intl.formatMessage(commonMessages.inactiveOption)}
              </Typography>
            ),
          },
        ];

        const bodyCellsMini: IBodyCell[] = [
          {
            label: '',
            cellComponent: (
              <SmallScreenCell resource={resource.toJS()} clubs={resource.get('clubs')} />
            ),
          },
        ];

        if (BOOKING_RESOURCES_EDIT) {
          btnEdit = <ActionItemEdit onClick={() => handleEditClick(resource.get('id'))} />;
        }

        if (BOOKING_RESOURCES_CHANGE_STATUS) {
          btnChangeStatus = (
            <ActionItemChangeStatus
              active={resource.get('active')}
              onClick={() => handleChangeStatusDialogShow(resource.toJS())}
            />
          );
        }

        if (btnEdit || btnChangeStatus) {
          const actionsMenu = (
            <ActionsMenu horizontal tableActionsMode>
              {btnEdit}
              {btnChangeStatus}
            </ActionsMenu>
          );

          bodyCells.push({
            label: 'actions',
            className: 'body-actions-cell',
            cellComponent: actionsMenu,
          });

          bodyCellsMini.push({
            label: '',
            align: 'center',
            padding: 'none',
            cellComponent: actionsMenu,
          });
        }

        return {
          id: resource.get('id'),
          cells: bodyCells,
          cellsMini: bodyCellsMini,
        };
      },
    )
    .toJS();

  const renderModals = (): JSX.Element => {
    return (
      <ChangeStatusModal
        itemTitle={selectedRow?.label || ''}
        isOpen={showChangeStatusDialog}
        onClose={handleChangeStatusDialogClose}
        onSubmit={handleChangeStatusResource}
        isActive={!!selectedRow?.active}
        isLoading={isStatusChangeLoading}
      />
    );
  };

  return (
    <>
      <Table
        title={<FormattedMessage {...tableHeaders.resources} />}
        addButtonRedirect="/booking/resources/new"
        headerOptions={headerOptions}
        showPerPageSelect
        isLoading={isLoading}
        rows={tableRows}
        filters={tableFilterSettings}
        tableParams={tableParams}
        totalRows={meta.get('total')}
        onChangeParams={onChangeParams}
        showAddButton={BOOKING_RESOURCES_CREATE}
      />
      {renderModals()}
    </>
  );
};

export default ResourcesTable;
